import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import { GET_PRODUCT_QUERY } from "apollo/queries/getProduct";
import { GET_USER_TRANSACTIONS } from "apollo/queries/userTransactions";
import Button from "components/Buttons";
import ModalComponent from "components/Modal/Orders";
import { useMutation } from "@apollo/client";
import { cancelOrder } from "apollo/mutations/cancelOrder";
import ModalStore from "components/Modal/ModalStore";
import advancedFormat from "dayjs/plugin/advancedFormat";
import dayjs from "dayjs";
import { formatString } from "utils";
import Loader from "components/Spinners/Loader";
import "./style.css";

dayjs.extend(advancedFormat);

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [CANCEL_ORDER, { loading: cancelLoading }] = useMutation(cancelOrder);

  const { data, loading } = useQuery(gql(GET_USER_TRANSACTIONS), {
    fetchPolicy: "network-only",
  });

  // Find the order by orderId
  const order = React.useMemo(() => {
    if (!data?.getUserTransactions) return null;

    const { boughtSaddles, soldSaddles } = data.getUserTransactions;
    const allOrders = [...boughtSaddles, ...soldSaddles];

    return allOrders.find((order) => order.orderId === orderId);
  }, [data, orderId]);

  // Fetch product details
  const { data: productData, loading: productLoading } = useQuery(
    GET_PRODUCT_QUERY,
    {
      variables: {
        brand: order?.brandName,
        sk: order?.productId,
      },
      fetchPolicy: "cache-first",
    }
  );

  const product = productData?.getProduct;

  const handleOrderCancel = async (orderId) => {
    try {
      await CANCEL_ORDER({
        variables: {
          id: orderId,
        },
        refetchQueries: ['getUserTransactions']
      });
      setOpenModal(false);
      ModalStore?.openSimpleSuccess({
        icon: undefined,
        fontSize: "40px",
        title: "Your cancel request has been submitted successfully.",
      });
    } catch (error) {
      console.error(error?.message);
    }
  };

  const handleBackToOrders = () => {
    navigate("/my-orders");
  };

  const handleViewReceipt = () => {
    if (order?.receiptUrl) {
      window.open(order.receiptUrl, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full w-full min-h-[400px]">
        <Loader
          filledColor="#2b364b"
          className="!h-[60px] !flex !items-center !text-center !justify-center"
        />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="w-[100%] mx-auto mb-[63px]">
        <div className="w-[90%] xl:max-w-[1300px] mx-auto mt-[121px] text-center">
          <h1 className="order_detail_title">Order Not Found</h1>
          <p className="order_detail_subtitle">
            The order you&apos;re looking for doesn&apos;t exist or you
            don&apos;t have permission to view it.
          </p>
          <Button onClick={handleBackToOrders} className="back_to_orders_btn">
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  const isOrderCancellable =
    !order?.isCancelRequestSent &&
    order?.shopify?.fulfillmentStatus !== "FULFILLED" &&
    order?.status !== "PENDING";

  return (
    <div className="w-[100%] mx-auto mb-[63px]">
      <div className="w-[90%] xl:max-w-[1300px] mx-auto mt-[121px]">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button onClick={handleBackToOrders} className="back_btn">
              ← Back to Orders
            </Button>
          </div>
          <h1 className="order_detail_title">Order Details</h1>
          <p className="order_detail_subtitle">Order ID: {order.orderId}</p>
        </div>

        {/* Order Status Card */}
        <div className="order_status_card">
          <div className="order_status_header">
            <h3 className="order_status_title">Order Status</h3>
            <span
              className={`status_badge ${order.orderStatus?.[0]?.status?.toLowerCase()}`}
            >
              {formatString(order.orderStatus?.[0]?.status)}
            </span>
          </div>
          <div className="order_status_details">
            <div className="status_item">
              <span className="status_label">Payment Status:</span>
              <span
                className={`payment_status ${order.paymentStatus?.toLowerCase()}`}
              >
                {formatString(order.paymentStatus)}
              </span>
            </div>
            {order.shopify?.fulfillmentStatus && (
              <div className="status_item">
                <span className="status_label">Fulfillment Status:</span>
                <span
                  className={`fulfillment_status ${order.shopify.fulfillmentStatus.toLowerCase()}`}
                >
                  {formatString(order.shopify.fulfillmentStatus)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Product Details */}
        {productLoading ? (
          <div className="product_loading_card">
            <div className="flex justify-center items-center py-8">
              <Loader
                filledColor="#2b364b"
                className="!h-[40px] !flex !items-center !text-center !justify-center"
              />
            </div>
          </div>
        ) : product ? (
          <div className="product_details_card">
            <h3 className="order_info_title">Product Details</h3>
            <div className="product_details_content">
              <div className="product_image_section">
                {(product.photo || (product.gallery && product.gallery.length > 0)) ? (
                  <img
                    src={product.photo || product.gallery[0]}
                    alt={product.productTitle || `${product.brandName} Saddle`}
                    className="product_image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const placeholder = e.target.parentElement.querySelector('.product_image_placeholder');
                      if (placeholder) {
                        placeholder.style.display = 'flex';
                      }
                    }}
                  />
                ) : (
                  <div className="product_image_placeholder">
                    <span>No Image Available</span>
                  </div>
                )}
              </div>
              <div className="product_info_section">
                <h4 className="product_title">{product.productTitle || `${product.brandName} Saddle`}</h4>
                <div className="product_meta">
                  {product.saddlefitSku && (
                    <div className="spec_item">
                      <span className="spec_label">SKU:</span>
                      <span className="spec_value">{product.saddlefitSku}</span>
                    </div>
                  )}
                  {product.price && (
                    <div className="spec_item">
                      <span className="spec_label">Price:</span>
                      <span className="spec_value">${product.price}</span>
                      {product.discount > 0 && (
                        <span className="discount_badge">-{product.discount}% OFF</span>
                      )}
                    </div>
                  )}
                  {product.quantity !== undefined && (
                    <div className="spec_item">
                      <span className="spec_label">Quantity:</span>
                      <span className="spec_value">{product.quantity}</span>
                    </div>
                  )}
                </div>
                {product.description && (
                  <p className="product_description" dangerouslySetInnerHTML={{ __html: product.description }} />
                )}
                <div className="product_specs">
                  {product.discipline && (
                    <div className="spec_item">
                      <span className="spec_label">Discipline:</span>
                      <span className="spec_value">{formatString(product.discipline)}</span>
                    </div>
                  )}
                  {product.newOrUsed && (
                    <div className="spec_item">
                      <span className="spec_label">Condition:</span>
                      <span className="spec_value">{formatString(product.newOrUsed)}</span>
                    </div>
                  )}
                  {product.size && (
                    <div className="spec_item">
                      <span className="spec_label">Size:</span>
                      <span className="spec_value">{product.size}</span>
                    </div>
                  )}
                  {product.type && (
                    <div className="spec_item">
                      <span className="spec_label">Type:</span>
                      <span className="spec_value">{formatString(product.type)}</span>
                    </div>
                  )}
                  {product.status && (
                    <div className="spec_item">
                      <span className="spec_label">Status:</span>
                      <span className="spec_value">{formatString(product.status)}</span>
                    </div>
                  )}
                  {product.score && (
                    <div className="spec_item">
                      <span className="spec_label">Score:</span>
                      <span className="spec_value">{product.score}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="product_not_found_card">
            <h3 className="order_info_title">Product Details</h3>
            <p className="product_not_found_text">
              Product information is not available for this order.
            </p>
          </div>
        )}

        {/* Order Information */}
        <div className="order_info_grid">
          {/* Order Details */}
          <div className="order_info_card">
            <h3 className="order_info_title">Order Information</h3>
            <div className="order_info_content">
              <div className="info_row">
                <span className="info_label">Order ID:</span>
                <span className="info_value">{order.orderId}</span>
              </div>
              <div className="info_row">
                <span className="info_label">Order Date:</span>
                <span className="info_value">
                  {dayjs(order.createdAt).format("MMM Do YYYY [at] h:mm A")}
                </span>
              </div>
              <div className="info_row">
                <span className="info_label">Brand:</span>
                <span className="info_value">{order.brandName}</span>
              </div>
              <div className="info_row">
                <span className="info_label">Source:</span>
                <span className="info_value">{order.source}</span>
              </div>
              {order.state && (
                <div className="info_row">
                  <span className="info_label">State:</span>
                  <span className="info_value">{order.state}</span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Information */}
          <div className="order_info_card">
            <h3 className="order_info_title">Pricing</h3>
            <div className="order_info_content">
              <div className="info_row">
                <span className="info_label">Product Price:</span>
                <span className="info_value">{order.productPrice}</span>
              </div>
              <div className="info_row">
                <span className="info_label">Total Price:</span>
                <span className="info_value total_price">
                  {order.totalPrice}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Timeline */}
        {order.orderStatus && order.orderStatus.length > 0 && (
          <div className="order_timeline_card">
            <h3 className="order_info_title">Order Timeline</h3>
            <div className="timeline">
              {order.orderStatus.map((status, index) => (
                <div key={index} className="timeline_item">
                  <div className="timeline_dot"></div>
                  <div className="timeline_content">
                    <div className="timeline_status">
                      {formatString(status.status)}
                    </div>
                    <div className="timeline_date">
                      {dayjs(status.timestamp).format(
                        "MMM Do YYYY [at] h:mm A"
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="order_actions">
          <div className="action_buttons">
            {order.receiptUrl && (
              <Button
                onClick={handleViewReceipt}
                className="action_btn secondary"
              >
                View Receipt
              </Button>
            )}
            <Button
              disabled={!isOrderCancellable}
              onClick={() => {
                setSelectedOrderId(order.id);
                setOpenModal(true);
              }}
              className={`action_btn primary ${!isOrderCancellable ? "disabled" : ""
                }`}
            >
              Cancel Order
            </Button>
          </div>
        </div>

        {/* Cancel Request Status */}
        {order.isCancelRequestSent && (
          <div className="cancel_request_notice">
            <p>A cancel request has already been submitted for this order.</p>
          </div>
        )}

        {order.isRefundRequestSent && (
          <div className="refund_request_notice">
            <p>A refund request has been submitted for this order.</p>
          </div>
        )}
      </div>

      {/* Cancel Order Modal */}
      <ModalComponent
        title={"Cancel Order"}
        modalIsOpen={openModal}
        loading={cancelLoading}
        setOpenModal={setOpenModal}
        onConfirm={() => handleOrderCancel(selectedOrderId)}
      >
        <p className={`modal_text_description`}>
          Are you sure you want to cancel this order?
        </p>
      </ModalComponent>
    </div>
  );
};

export default OrderDetail;
